import { observer } from 'mobx-react';
import * as React from 'react';

import { withI18n, WithI18nProps } from '../../../../internationalization/i18n-hoc';
import { PlotKeyToggleState } from '../../../../stores/plot-key-toggle-state';
import { SVGBuilder } from '../../../../ui/components/svg-builder/svg-builder';
import { RoleBuilderHelper, RoleBuilderPlot } from '../../../stores';

const styles = require('../../../../ui/components/competency-bars/competency-bars.css');

export interface RoleBuilderCompetencyBarsProps extends WithI18nProps {
  competencyId: string;
  helper: RoleBuilderHelper;
  plotKeyToggleState: PlotKeyToggleState;
}

interface BarData {
  width: number;
  scoreText: string;
}

/** Circular Progress Bar  */
@observer
export class RoleBuilderCompetencyBars0 extends React.Component<RoleBuilderCompetencyBarsProps> {
  private readonly size: number = 500;

  private readonly widthMargin = 10;

  private readonly xStart = this.widthMargin;
  private readonly xEnd = 100 - this.xStart;

  render() {
    const { helper, t } = this.props;

    const skills = helper.getAssignedSkillsByCompetency(this.props.competencyId, t) || [];

    const lineOffset = 4;
    const titleOffset = 8.5;
    const yGap = 20;

    const dimY =
      this.scale(lineOffset + titleOffset + yGap + yGap * skills.length);

    const builder = new SVGBuilder(1, {
      posX: 0,
      posY: 0,
      dimX: this.size,
      dimY
    });

    const ranges = helper.ranges
      .slice()
      .map(r => r.value)
      .sort((a, b) => a.scoreRange.range.v2 - b.scoreRange.range.v2);

    const factor = (100 - 2 * this.widthMargin) / 100;
    const extents: number[] = ranges.map(r => (r.scoreRange.range.v2 - r.scoreRange.range.v1 + 1) * factor);
    // Remove the last extent
    extents.pop();

    // Range Markers. Allow for more than 3 and for different labels
    // Initialize
    let xOffset = this.xStart;

    const separators = [
      this.xStart,
      ...extents.map(extent => {
        const separator = xOffset + extent;
        xOffset = separator;
        return separator;
      }),
      this.xEnd
    ].map(this.scale);

    let yOffset = lineOffset;

    for (const x of separators) {
      builder.addLine({
        x1: x,
        y1: this.scale(yOffset - 2),
        x2: x,
        y2: this.scale(yOffset),
        style: 'solid',
        stroke: 'light'
      });
      if (x !== this.scale(this.xStart) && x !== this.scale(this.xEnd)) {
        builder.addLine({
          x1: x,
          y1: this.scale(yOffset),
          x2: x,
          y2: dimY,
          style: 'dashed',
          stroke: 'faint'
        });
      }
    }
    builder.addLine({
      x1: this.scale(this.xStart),
      y1: this.scale(yOffset),
      x2: this.scale(this.xEnd),
      y2: this.scale(yOffset),
      style: 'solid',
      stroke: 'light'
    });

    // Range Markers. Allow for more than 3 and for different labels
    // Re-initialize
    xOffset = this.xStart;

    ranges.map(r => {
      builder.addText({
        x: this.scale(xOffset + ((r.scoreRange.range.v2 - r.scoreRange.range.v1 + 1) * factor) / 2),
        y: this.scale(yOffset - 2),
        text: t(r.scoreRange.label),
        anchor: 'center',
        style: 'detail'
      });
      xOffset = xOffset + (r.scoreRange.range.v2 - r.scoreRange.range.v1 + 1) * factor;
    });

    yOffset += titleOffset;

    {
      /// Display the full name here
      const title = t(helper.assignedCompetency(this.props.competencyId).fullName);

      const barHeight = 3;

      this.renderBarCollection(builder, {
        helper,
        title,
        barHeight,
        style: 'title',
        yOffset,
        id: this.props.competencyId
      });
    }

    /// skill bars
    for (const skill of skills) {
      yOffset += yGap;
      const title = t(skill.value.name); 
      const barHeight = 2;

      this.renderBarCollection(builder, {
        helper,
        title,
        barHeight,
        style: 'subtitle',
        yOffset,
        id: skill.id
      });
    }

    return <div className={styles.container}>{builder.build()}</div>;
  }

  renderBarCollection(
    builder: SVGBuilder,
    {
      helper,
      yOffset,
      id,
      title,
      barHeight,
      style
    }: RoleBuilderBarCollectionProps
  ) {
    const iconWidth = 0;
    const margin = 0;

    builder.addText({
      x: this.scale(this.xStart + iconWidth + margin),
      y: this.scale(yOffset),
      text: title,
      anchor: 'right',
      style
    });

    // const lastYOffset =
    this.addProgressBar(builder, yOffset, helper, barHeight, id !== this.props.competencyId ? id : undefined);

  }

  getWidthAndScoreTextFromPlotKey(props: RoleBuilderProgressBarProps): BarData | undefined {
    const score = this.getScoreFromPlotKey(props);
    if (score === undefined) {
      return undefined;
    } else {
      return { width: this.getWidthFromScore(score), scoreText: `${score}` };
    }
  }

  getScoreFromPlotKey(props: RoleBuilderProgressBarProps): number | undefined {
    if (!props.helper.plotExists(props.plotkey)) {
      return undefined;
    }
    if (props.kind === 'competency') {
      return props.helper.score(props.plotkey, {
        kind: 'competency',
        competencyId: this.props.competencyId
      });
    } else {
      return props.helper.score(props.plotkey, {
        kind: 'skill',
        skillId: props.skillId
      });
    }
  }

  getWidthFromScore(score: number): number {
    return (score * (this.xEnd - this.xStart)) / 100;
  }


  addProgressBar(
    builder: SVGBuilder,
    yOffset: number,
    helper: RoleBuilderHelper,
    rectHeight: number,
    skillId?: string
  ): number {
    let localYOffset = yOffset;
    const backgroundWidth = this.getWidthFromScore(100);
    const expectedBarData = this.getWidthAndScoreTextFromPlotKey(
      skillId === undefined
        ? { kind: 'competency', helper, plotkey: 'expected' }
        : { kind: 'skill', helper, plotkey: 'expected', skillId }
    );
    const industryBarData = this.getWidthAndScoreTextFromPlotKey(
      skillId === undefined
        ? { kind: 'competency', helper, plotkey: 'industry' }
        : { kind: 'skill', helper, plotkey: 'industry', skillId }
    );

    const barOffset = 5;
    localYOffset += barOffset;

    builder.addRect({
      x: this.scale(this.xStart),
      y: this.scale(localYOffset),
      style: 'white',
      width: this.scale(backgroundWidth),
      height: this.scale(rectHeight)
    });

    if (expectedBarData !== undefined && this.props.plotKeyToggleState.isShown('expected')) {
      const desiredMarkerY1 = localYOffset - 1.5;
      builder.addRect({
        x: this.scale(this.xStart),
        y: this.scale(localYOffset),
        style: 'grey',
        width: this.scale(expectedBarData.width),
        height: this.scale(rectHeight)
      });
      builder.addLine({
        x1: this.scale(expectedBarData.width + this.xStart),
        y1: this.scale(desiredMarkerY1),
        x2: this.scale(expectedBarData.width + this.xStart),
        y2: this.scale(localYOffset + rectHeight),
        style: 'solid'
      });
      builder.addCircle({
        x: this.scale(expectedBarData.width + this.xStart),
        y: this.scale(desiredMarkerY1),
        r: this.scale(0.7),
        style: 'filled'
      });
      builder.addText({
        x: this.scale(expectedBarData.width + this.xStart - 2),
        y: this.scale(desiredMarkerY1) + 1,
        text: expectedBarData.scoreText,
        style: 'marker',
        anchor: 'left'
      });
    }

    const plotOffset = 2;
    localYOffset += rectHeight + plotOffset;

    localYOffset += plotOffset;

    localYOffset += plotOffset;
    if (industryBarData !== undefined && this.props.plotKeyToggleState.isShown('industry')) {
      this.renderIndustryBar(builder, industryBarData.width, localYOffset, plotOffset, industryBarData.scoreText);
    }

    /// Return the current yOffset plus the right amount of space to add
    return localYOffset + plotOffset;
  }


  // Also used for comparisonRole
  renderPreviousBar(
    builder: SVGBuilder,
    width: number,
    yOffset: number,
    plotOffset: number,
    scoreText: string
  ) {
    builder.addLine({
      x1: this.scale(this.xStart),
      y1: this.scale(yOffset),
      x2: this.scale(width + this.xStart),
      y2: this.scale(yOffset),
      style: 'dashed',
      stroke: 'dark'
    });
    builder.addRect({
      x: this.scale(this.xStart),
      y: this.scale(yOffset - plotOffset / 2),
      width: this.scale(width + 0.7),
      height: this.scale(plotOffset),
      style: 'transparent'
    });
    builder.addText({
      x: this.scale(width + this.xStart - 2),
      y: this.scale(yOffset - 1.5) + 1,
      text: scoreText,
      style: 'marker',
      anchor: 'left'
    });
  }

  renderGroupBar(
    builder: SVGBuilder,
    width: number,
    yOffset: number,
    plotOffset: number,
    scoreText: string
  ) {
    builder.addLine({
      x1: this.scale(this.xStart),
      y1: this.scale(yOffset),
      x2: this.scale(width + this.xStart),
      y2: this.scale(yOffset),
      style: 'dotted'
    });
    builder.addCircle({
      x: this.scale(width + this.xStart),
      y: this.scale(yOffset),
      style: 'shaded',
      r: this.scale(0.7)
    });
    builder.addRect({
      x: this.scale(this.xStart),
      y: this.scale(yOffset - plotOffset / 2),
      width: this.scale(width + 0.7),
      height: this.scale(plotOffset),
      style: 'transparent'
    });
    builder.addText({
      x: this.scale(width + this.xStart - 2),
      y: this.scale(yOffset - 1.5) + 1,
      text: scoreText,
      style: 'marker',
      anchor: 'left'
    });
  }

  renderIndustryBar(
    builder: SVGBuilder,
    width: number,
    yOffset: number,
    plotOffset: number,
    scoreText: string
  ) {
    builder.addLine({
      x1: this.scale(this.xStart),
      y1: this.scale(yOffset),
      x2: this.scale(width + this.xStart),
      y2: this.scale(yOffset),
      style: 'dashed'
    });
    builder.addTriangle({
      x: this.scale(width + this.xStart),
      y: this.scale(yOffset),
      size: this.scale(0.7)
    });
    builder.addRect({
      x: this.scale(this.xStart),
      y: this.scale(yOffset - plotOffset / 2),
      width: this.scale(width + 0.7),
      height: this.scale(plotOffset),
      style: 'transparent'
    });
    builder.addText({
      x: this.scale(width + this.xStart - 2),
      y: this.scale(yOffset - 1.5) + 1,
      text: scoreText,
      style: 'marker',
      anchor: 'left'
    });
  }

  scale = (num: number): number => {
    return (num * this.size) / 100;
  };
}

export const RoleBuilderCompetencyBars = withI18n(RoleBuilderCompetencyBars0);

type RoleBuilderProgressBarProps =
  | {
      kind: 'competency';
      helper: RoleBuilderHelper;
      plotkey: keyof RoleBuilderPlot;
    }
  | {
      kind: 'skill';
      helper: RoleBuilderHelper;
      plotkey: keyof RoleBuilderPlot;
      skillId: string;
    };

interface RoleBuilderBarCollectionProps {
  helper: RoleBuilderHelper;
  id: string;
  yOffset: number;
  title: string;
  barHeight: number;
  style: 'title' | 'subtitle';
}

/**
 * Code snippets to split a string into an array of wrapped text
 * https://medium.com/@CarysMills/wrapping-svg-text-without-svg-2-ecbfb58f7ba4
 */

function measureText(text: string, font: string) {
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  if (context === null) {
    throw Error('Context is null');
  }
  context.font = font;
  return context.measureText(text);
}

export function getTextHeight(text: string, font: string) {
  return measureText(text, font).actualBoundingBoxAscent;
}

interface InfoIconProps {
  key: number | string;
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  onMouseEnter?(): void;
  onMouseLeave?(): void;
  iconCount?: number;
}

/// FF2-436 add data-tour attribute for walkthrough
export function infoIcon(props: InfoIconProps): JSX.Element {
  return (
    <svg
      id="Layer_1"
      key={props.key}
      x={props.x}
      y={props.y}
      width={props.width}
      height={props.height}
      data-name="Layer 1"
      viewBox="0 0 25.5 25.5"
      onMouseEnter={props.onMouseEnter}
      onMouseLeave={props.onMouseLeave}
    >
      <defs>
        <clipPath id="clip-path">
          <circle cx="12.75" cy="12.75" r="12" style={{ fill: 'none' }} />
        </clipPath>
      </defs>
      {/* <title>e </title> */}
      <g style={{ clipPath: 'url(#clip-path)' }}>
        <rect
          x="-4.25"
          y="-4.25"
          width="34"
          height="34"
          style={{ fill: '#fff', opacity: 0 }}
          data-tour={`infoIcon:${props.iconCount}`}
        />
      </g>
      <rect x="11.25" y="11.75" width="3" height="8" style={{ fill: '#e16e46' }} />
      <circle cx="12.75" cy="7.75" r="2" style={{ fill: '#e16e46' }} />
      <circle
        cx="12.75"
        cy="12.75"
        r="12"
        style={{
          fill: 'none',
          stroke: '#e16e46',
          strokeLinejoin: 'round',
          strokeWidth: '1.5px'
        }}
      />
    </svg>
  );
}



// WEBPACK FOOTER //
// ./src/admin/ui/components/role-builder-competency-bars/role-builder-competency-bars.tsx